import {
  batchExpirySnapshot,
  classifyExpiryRatio,
  expiryUrgency,
  shanghaiDateString,
  type BatchExpirySnapshot,
} from "./batch";
import {
  normalizeLocationCode,
  type WarehouseMapLayout,
} from "./warehouse";
import type {
  BatchRecord,
  DecisionPlan,
  ExpiryClass,
  LocationNode,
  ProductAnalysis,
  VelocityClass,
  WarehouseDataset,
} from "../types";

export type ExpiryCategory = Exclude<ExpiryClass, "none">;
export type ExpiryCategoryFilter = "all" | ExpiryCategory;
export type ExpiryViewFilter = ExpiryCategoryFilter | "risk";

export interface ExpiryBatchInsight {
  batch: BatchRecord;
  snapshot: BatchExpirySnapshot;
  category: VelocityClass;
  forecastDaily: number;
  currentNode?: LocationNode;
  currentDistance: number | null;
  recommendedRack: string | null;
  recommendedDistance: number | null;
  recommendedBand: "前沿加速带" | "中区周转带" | "后区存储带";
  placementScore: number;
  priorityLabel: "P0" | "P1" | "P2" | "P3";
  daysToNextClass: number | null;
  nextClass: ExpiryCategory | null;
  warning: boolean;
}

export interface ExpiryProductInsight {
  sku: string;
  product: string;
  category: VelocityClass;
  forecastDaily: number;
  batches: ExpiryBatchInsight[];
  quantityByCategory: Map<ExpiryCategory, number>;
  totalQuantity: number;
  locations: string[];
  earliestExpiry: string;
  mostUrgentClass: ExpiryCategory;
  representative: ExpiryBatchInsight;
  daysToNextClass: number | null;
}

export interface ExpiryCategorySummary {
  category: ExpiryCategory;
  label: string;
  productCount: number;
  batchCount: number;
  quantity: number;
  warningCount: number;
}

export interface ExpiryWarning {
  id: string;
  severity: "critical" | "warning" | "notice";
  kind: "threshold" | "expired" | "unknown" | "recalled" | "near-expiry";
  title: string;
  detail: string;
  sku: string;
  product: string;
  location: string;
  recommendedRack: string | null;
  days: number | null;
  category: ExpiryCategory;
}

export interface ExpiryInsights {
  today: string;
  batches: ExpiryBatchInsight[];
  products: ExpiryProductInsight[];
  categories: ExpiryCategorySummary[];
  warnings: ExpiryWarning[];
  warningCount: number;
  urgentCount: number;
  recommendedLocations: Set<string>;
}

const CATEGORY_ORDER: ExpiryCategory[] = [
  "quarter",
  "third",
  "half",
  "fresh",
  "expired",
  "unknown",
];

const CATEGORY_LABELS: Record<ExpiryCategory, string> = {
  quarter: "¼ 临期",
  third: "⅓ 临期",
  half: "½ 临期",
  fresh: "新鲜期",
  expired: "已过期",
  unknown: "待核对",
};

const POPULARITY_SCORE: Record<VelocityClass, number> = {
  爆旺: 300,
  平销: 180,
  平滞: 60,
};

const EXPIRY_SCORE: Record<ExpiryCategory, number> = {
  quarter: 140,
  third: 112,
  half: 84,
  fresh: 28,
  expired: -120,
  unknown: -80,
};

/**
 * 位置分配的剩余期限权重：批次到期日期距今天数占总周期的百分比越低，
 * 越应靠近出货口（前沿带），尽快出库。这里把「剩余天数百分比」映射为
 * 一个连续权重，百分比越低权重越高，从而让排序平滑地反映剩余期限。
 */
function remainingRatioWeight(remainingRatio: number | null): number {
  if (remainingRatio == null || !Number.isFinite(remainingRatio)) return 0;
  // remainingRatio ∈ [-1, 1]，1 表示刚生产、0 表示到期、负数表示已过期。
  const clamped = Math.max(0, Math.min(1, remainingRatio));
  // 剩余比例 0 → 满分 100；剩余比例 1 → 0 分，线性递减。
  return Math.round((1 - clamped) * 100);
}

function nextExpiryBoundary(category: ExpiryCategory): {
  nextClass: ExpiryCategory;
  ratio: number;
} | null {
  if (category === "fresh") return { nextClass: "half", ratio: 0.5 };
  if (category === "half") return { nextClass: "third", ratio: 1 / 3 };
  if (category === "third") return { nextClass: "quarter", ratio: 0.25 };
  if (category === "quarter") return { nextClass: "expired", ratio: 0 };
  return null;
}

function thresholdDays(snapshot: BatchExpirySnapshot) {
  const boundary = nextExpiryBoundary(snapshot.expiryClass as ExpiryCategory);
  if (!boundary || snapshot.remainingDays == null || !snapshot.shelfLifeDays) {
    return { nextClass: boundary?.nextClass ?? null, daysToNextClass: null };
  }
  const boundaryDay = boundary.ratio * snapshot.shelfLifeDays;
  return {
    nextClass: boundary.nextClass,
    daysToNextClass: Math.max(0, Math.ceil(snapshot.remainingDays - boundaryDay)),
  };
}

function latestCategoryBySku(dataset: WarehouseDataset) {
  const rows = new Map<string, { date: string; category: VelocityClass }>();
  dataset.sales.forEach((sale) => {
    if (!sale.category) return;
    const current = rows.get(sale.sku);
    if (!current || sale.date > current.date) {
      rows.set(sale.sku, {
        date: sale.date,
        category: sale.category as VelocityClass,
      });
    }
  });
  return rows;
}

function bandForDistance(distance: number): ExpiryBatchInsight["recommendedBand"] {
  if (distance <= 45) return "前沿加速带";
  if (distance <= 85) return "中区周转带";
  return "后区存储带";
}

function priorityForScore(score: number): ExpiryBatchInsight["priorityLabel"] {
  if (score >= 420) return "P0";
  if (score >= 340) return "P1";
  if (score >= 240) return "P2";
  return "P3";
}

function preferredBandForPriority(
  priority: ExpiryBatchInsight["priorityLabel"],
): ExpiryBatchInsight["recommendedBand"] {
  if (priority === "P0" || priority === "P1") return "前沿加速带";
  if (priority === "P2") return "中区周转带";
  return "后区存储带";
}

function isWarningEligible(category: ExpiryCategory, days: number | null) {
  return (
    category === "expired" ||
    category === "unknown" ||
    (days != null && days <= 21) ||
    (category === "quarter")
  );
}

export function expiryCategoryLabel(category: ExpiryCategoryFilter) {
  return category === "all" ? "全部效期" : CATEGORY_LABELS[category];
}

export function buildExpiryInsights(
  dataset: WarehouseDataset,
  plan: DecisionPlan,
  layout: WarehouseMapLayout,
  today = shanghaiDateString(),
): ExpiryInsights {
  const planBySku = new Map<string, ProductAnalysis>(
    plan.products.map((product) => [product.sku, product]),
  );
  const latestSalesCategory = latestCategoryBySku(dataset);
  const nodeByLocation = new Map(layout.nodes.map((node) => [node.code, node]));
  const candidateRacks = layout.nodes
    .filter((node) => node.stockState !== "stocked")
    .sort((a, b) => {
      const stateRank = { empty: 0, unknown: 1, stocked: 2 };
      return stateRank[a.stockState] - stateRank[b.stockState]
        || a.distance - b.distance;
    });

  const rankedBatches = dataset.batches
    .filter((batch) => Number.isFinite(batch.quantity) && batch.quantity > 0)
    .map<ExpiryBatchInsight>((batch) => {
      const snapshot = batchExpirySnapshot(batch, today);
      const category = snapshot.expiryClass as ExpiryCategory;
      const product = planBySku.get(batch.sku);
      const popularity =
        product?.category
        ?? latestSalesCategory.get(batch.sku)?.category
        ?? "平销";
      const location = normalizeLocationCode(batch.location);
      const currentNode = nodeByLocation.get(location);
      const threshold = thresholdDays(snapshot);
      const ratioWeight = remainingRatioWeight(snapshot.remainingRatio);
      const score =
        POPULARITY_SCORE[popularity]
        + EXPIRY_SCORE[category]
        + ratioWeight
        + Math.max(0, 28 - Math.min(28, threshold.daysToNextClass ?? 28)) * 0.45;
      return {
        batch,
        snapshot,
        category: popularity,
        forecastDaily: product?.forecastDaily ?? 0,
        currentNode,
        currentDistance: currentNode?.distance ?? null,
        recommendedRack: null,
        recommendedDistance: null,
        recommendedBand: bandForDistance(currentNode?.distance ?? 999),
        placementScore: Number(score.toFixed(1)),
        priorityLabel: priorityForScore(score),
        daysToNextClass: threshold.daysToNextClass,
        nextClass: threshold.nextClass,
        warning: batch.recallFlag || isWarningEligible(category, threshold.daysToNextClass),
      };
    })
    .sort((a, b) => {
      if (Boolean(a.batch.recallFlag) !== Boolean(b.batch.recallFlag)) {
        return a.batch.recallFlag ? -1 : 1;
      }
      if (a.priorityLabel !== b.priorityLabel) {
        return a.priorityLabel.localeCompare(b.priorityLabel);
      }
      return b.placementScore - a.placementScore
        || (a.snapshot.remainingDays ?? Number.MAX_SAFE_INTEGER)
          - (b.snapshot.remainingDays ?? Number.MAX_SAFE_INTEGER)
        || a.batch.expiresOn.localeCompare(b.batch.expiresOn);
    });

  const candidatesByBand = new Map<
    ExpiryBatchInsight["recommendedBand"],
    LocationNode[]
  >([
    ["前沿加速带", []],
    ["中区周转带", []],
    ["后区存储带", []],
  ]);
  candidateRacks.forEach((node) => {
    candidatesByBand.get(bandForDistance(node.distance))?.push(node);
  });
  const assignedCandidates = new Set<string>();

  function takeCandidate(
    preferredBand: ExpiryBatchInsight["recommendedBand"],
  ): LocationNode | undefined {
    const preferred = candidatesByBand
      .get(preferredBand)
      ?.find((node) => !assignedCandidates.has(node.code));
    if (preferred) {
      assignedCandidates.add(preferred.code);
      return preferred;
    }
    const fallback = candidateRacks.find((node) => !assignedCandidates.has(node.code));
    if (fallback) assignedCandidates.add(fallback.code);
    return fallback;
  }

  const batches = rankedBatches.map((row) => {
    if (
      row.snapshot.expiryClass === "expired"
      || row.snapshot.expiryClass === "unknown"
      || row.batch.recallFlag
    ) {
      return row;
    }
    const recommendedNode = takeCandidate(preferredBandForPriority(row.priorityLabel));
    return {
      ...row,
      recommendedRack: recommendedNode?.code ?? null,
      recommendedDistance: recommendedNode?.distance ?? null,
      recommendedBand: recommendedNode
        ? bandForDistance(recommendedNode.distance)
        : row.recommendedBand,
    };
  });

  const grouped = new Map<string, ExpiryBatchInsight[]>();
  batches.forEach((batch) => {
    const rows = grouped.get(batch.batch.sku) ?? [];
    rows.push(batch);
    grouped.set(batch.batch.sku, rows);
  });

  const products = [...grouped.entries()]
    .map<ExpiryProductInsight>(([sku, rows]) => {
      const representative = [...rows].sort(
        (a, b) =>
          b.placementScore - a.placementScore
          || expiryUrgency(a.snapshot.expiryClass) - expiryUrgency(b.snapshot.expiryClass),
      )[0];
      const quantityByCategory = new Map<ExpiryCategory, number>();
      rows.forEach((row) => {
        quantityByCategory.set(
          row.snapshot.expiryClass as ExpiryCategory,
          (quantityByCategory.get(row.snapshot.expiryClass as ExpiryCategory) ?? 0)
            + Math.max(0, row.batch.quantity),
        );
      });
      const mostUrgentClass = CATEGORY_ORDER.find(
        (category) => (quantityByCategory.get(category) ?? 0) > 0,
      ) ?? "unknown";
      const earliest = rows
        .map((row) => row.snapshot.expiresOn)
        .filter(Boolean)
        .sort()[0] ?? "";
      return {
        sku,
        product: representative.batch.product,
        category: representative.category,
        forecastDaily: representative.forecastDaily,
        batches: rows,
        quantityByCategory,
        totalQuantity: rows.reduce((sum, row) => sum + Math.max(0, row.batch.quantity), 0),
        locations: [...new Set(rows.map((row) => normalizeLocationCode(row.batch.location)).filter(Boolean))],
        earliestExpiry: earliest,
        mostUrgentClass,
        representative,
        daysToNextClass: rows
          .map((row) => row.daysToNextClass)
          .filter((value): value is number => value != null)
          .sort((a, b) => a - b)[0] ?? null,
      };
    })
    .sort((a, b) => {
      if (Boolean(a.representative.batch.recallFlag) !== Boolean(b.representative.batch.recallFlag)) {
        return a.representative.batch.recallFlag ? -1 : 1;
      }
      return b.representative.placementScore - a.representative.placementScore
        || a.earliestExpiry.localeCompare(b.earliestExpiry);
    });

  const categories = CATEGORY_ORDER.map<ExpiryCategorySummary>((category) => {
    const rows = batches.filter((batch) => batch.snapshot.expiryClass === category);
    return {
      category,
      label: CATEGORY_LABELS[category],
      productCount: new Set(rows.map((row) => row.batch.sku)).size,
      batchCount: rows.length,
      quantity: rows.reduce((sum, row) => sum + Math.max(0, row.batch.quantity), 0),
      warningCount: rows.filter((row) => row.warning).length,
    };
  });

  const warnings = batches
    .flatMap<ExpiryWarning>((row) => {
      const normalizedLocation = normalizeLocationCode(row.batch.location);
      const base = {
        sku: row.batch.sku,
        product: row.batch.product,
        location: normalizedLocation || row.batch.location || "未提供",
        recommendedRack: row.recommendedRack,
        days: row.snapshot.remainingDays,
        category: row.snapshot.expiryClass as ExpiryCategory,
      };
      const result: ExpiryWarning[] = [];
      if (row.batch.recallFlag) {
        result.push({
          ...base,
          id: `${row.batch.id}-recall`,
          severity: "critical",
          kind: "recalled",
          title: "召回批次已冻结",
          detail: `批次 ${row.batch.lot} 不参与自动履约，请立即隔离复核。`,
        });
      }
      if (row.snapshot.expiryClass === "unknown") {
        result.push({
          ...base,
          id: `${row.batch.id}-unknown`,
          severity: "critical",
          kind: "unknown",
          title: "效期字段待核对",
          detail: `批次 ${row.batch.lot} 缺少可信到期依据，暂不建议出库。`,
        });
      } else if (row.snapshot.expiryClass === "expired") {
        result.push({
          ...base,
          id: `${row.batch.id}-expired`,
          severity: "critical",
          kind: "expired",
          title: "批次已过期",
          detail: `到期 ${row.snapshot.expiresOn || "未知"}，需退出可售库存。`,
        });
      } else if (row.daysToNextClass != null && row.daysToNextClass <= 21) {
        result.push({
          ...base,
          id: `${row.batch.id}-threshold`,
          severity: row.daysToNextClass <= 7 ? "critical" : "warning",
          kind: "threshold",
          title: row.daysToNextClass === 0
            ? `今日进入 ${expiryCategoryLabel(row.nextClass ?? "quarter")}`
            : `${row.daysToNextClass} 天后进入 ${expiryCategoryLabel(row.nextClass ?? "quarter")}`,
          detail: `批次 ${row.batch.lot} 正接近下一效期节点，建议按 ¼ / ⅓ / ½ 分类提前调配。`,
        });
      } else if (
        row.snapshot.remainingDays != null
        && row.snapshot.remainingDays <= 30
        && !result.length
      ) {
        result.push({
          ...base,
          id: `${row.batch.id}-near`,
          severity: "notice",
          kind: "near-expiry",
          title: "进入效期近距离观察",
          detail: `批次 ${row.batch.lot} 已进入下一效期节点观察区，请结合 ¼ / ⅓ / ½ 分类安排出库。`,
        });
      }
      return result;
    })
    .sort((a, b) => {
      const rank = { critical: 0, warning: 1, notice: 2 };
      return rank[a.severity] - rank[b.severity]
        || (a.days ?? Number.MAX_SAFE_INTEGER) - (b.days ?? Number.MAX_SAFE_INTEGER);
    });

  return {
    today,
    batches,
    products,
    categories,
    warnings,
    warningCount: batches.filter((batch) => batch.warning).length,
    urgentCount: batches.filter(
      (batch) => batch.snapshot.expiryClass === "quarter"
        || batch.snapshot.expiryClass === "expired"
        || batch.snapshot.expiryClass === "unknown"
        || batch.batch.recallFlag,
    ).length,
    recommendedLocations: new Set(
      batches.map((batch) => batch.recommendedRack).filter((code): code is string => Boolean(code)),
    ),
  };
}

export function productMatchesExpiryCategory(
  product: ExpiryProductInsight,
  category: ExpiryCategoryFilter,
) {
  return category === "all" || (product.quantityByCategory.get(category) ?? 0) > 0;
}

export function batchMatchesExpiryCategory(
  batch: ExpiryBatchInsight,
  category: ExpiryCategoryFilter,
) {
  return category === "all" || batch.snapshot.expiryClass === category;
}

export function batchMatchesExpiryView(
  batch: ExpiryBatchInsight,
  filter: ExpiryViewFilter,
) {
  return filter === "risk" ? batch.warning : batchMatchesExpiryCategory(batch, filter);
}

export function productMatchesExpiryView(
  product: ExpiryProductInsight,
  filter: ExpiryViewFilter,
) {
  return filter === "risk"
    ? product.batches.some((batch) => batch.warning)
    : productMatchesExpiryCategory(product, filter);
}

export function ratioClassForProduct(product: ExpiryProductInsight) {
  return classifyExpiryRatio(
    product.representative.snapshot.remainingRatio,
  );
}
